import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import OutlinedInput from '@mui/material/OutlinedInput';
import Paper from '@mui/material/Paper';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { useEffect, useState } from 'react';
import Barcode from 'react-barcode';
import { formatDateForInput, getToday, isEmpty } from 'src/common/helper';
import BtnGradient from 'src/components/common/button/BtnGradient';
import BtnOutlineGradient from 'src/components/common/button/BtnOutlineGradient';
import { useNotify } from 'src/context/ContextNotify';
import { putAPI } from 'src/utils/api';
import { getToken } from 'src/utils/auth';

const dateStyle = {
  width: '100%',
  height: '32px',
  my: '8px',
  '& .MuiInputBase-input.MuiOutlinedInput-input': {
    py: '8px',
    fontSize: '14px',
  },
};

const inputStyle = {
  fontSize: '14px',
  my: '8px',
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#BDBDBD',
  },
  '& .MuiInputBase-input': { padding: '8px 14px' },
  '& input::placeholder': {
    fontSize: '14px',
    fontWeight: 500,
    color: '#CCD1D9',
    opacity: 1,
  },
  backgroundColor: 'primary.light',
  width: '100%',
};

const defaultError = {
  patient_name: true,
  patient_number: true,
  creation_date: true,
  expiry_date: true,
  storage_date: true,
  total_quantity: true,
  log_date: true,
};

type DataType = {
  barcode: string;
  patient_name: string;
  patient_number: string;
  creation_date: Dayjs | null;
  expiry_date: Dayjs | null;
  storage_date: Dayjs | null;
  total_quantity: number;
  status: string;
  used_quantity: number;
  used_log: any[];
};

const DEFAULT_DATA = {
  barcode: '',
  patient_name: '',
  patient_number: '',
  creation_date: null,
  expiry_date: null,
  storage_date: null,
  total_quantity: 0,
  status: '',
  used_quantity: 0,
  used_log: [],
};

const DialogEditInformation = (props: any) => {
  const notifyContext = useNotify();
  const [patientInfo, setPatientInfo] = useState<DataType>(DEFAULT_DATA);
  const [errors, setErrors] = useState(defaultError);
  const handleInputChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target;
    if (name === 'total_quantity') {
      let valueTotal = Number(value);
      if (valueTotal < 0) valueTotal = 0;
      setPatientInfo((prevState) => ({
        ...prevState,
        [name]: valueTotal,
      }));
      return;
    }
    setPatientInfo((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleDateChange = (newValue: dayjs.Dayjs | null, name: string) => {
    if (name === 'creation_date') {
      const { expiry_date, storage_date } = calculateDates(newValue);
      setPatientInfo((prevState) => ({
        ...prevState,
        creation_date: dayjs(newValue),
        expiry_date: dayjs(expiry_date),
        storage_date: dayjs(storage_date),
      }));
      return;
    }

    setPatientInfo((prevState) => ({
      ...prevState,
      [name]: dayjs(newValue),
    }));
  };

  const calculateDates = (creation_date: any) => {
    if (!creation_date) return { expiry_date: '', storage_date: '' };

    const creation_dateObj = new Date(creation_date);
    try {
      formatDateForInput(creation_dateObj);
    } catch (error) {
      return { expiry_date: '', storage_date: '' };
    }

    const expiry_dateObj = new Date(creation_dateObj);
    expiry_dateObj.setMonth(expiry_dateObj.getMonth() + 6);

    const storage_dateObj = new Date(creation_dateObj);
    storage_dateObj.setFullYear(storage_dateObj.getFullYear() + 1);

    return {
      expiry_date: formatDateForInput(expiry_dateObj),
      storage_date: formatDateForInput(storage_dateObj),
    };
  };

  useEffect(() => {
    if (props.inventoryUpdate) {
      let patient = {
        ...props.inventoryUpdate,
        creation_date: dayjs(props.inventoryUpdate.creation_date),
        expiry_date: dayjs(props.inventoryUpdate.expiry_date),
        storage_date: dayjs(props.inventoryUpdate.storage_date),
        used_log: props.inventoryUpdate.used_log.map((log: any) => {
          return {
            ...log,
            date: dayjs(log.date),
          };
        }),
      };

      setPatientInfo(patient);
    }
  }, [props.inventoryUpdate]);

  const handleDateLogChange = (newValue: dayjs.Dayjs | null, index: number) => {
    let modifiedData = [...patientInfo.used_log];

    modifiedData[index] = {
      ...modifiedData[index],
      date: newValue,
    };

    setPatientInfo({
      ...patientInfo,
      used_log: modifiedData,
    });
  };

  const handleInputLogChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    index: number
  ) => {
    let value = Number(event.target.value);
    if (value < 1) value = 1;
    let modifiedData = [...patientInfo.used_log];

    modifiedData[index] = {
      ...modifiedData[index],
      used_count: value,
    };

    const used_quantity = modifiedData.reduce((accumulator, currentValue) => {
      return accumulator + Number(currentValue.used_count);
    }, 0);

    setPatientInfo({
      ...patientInfo,
      used_log: modifiedData,
      used_quantity: used_quantity,
    });
  };

  const handleStatusLogChange = (event: SelectChangeEvent, index: number) => {
    const { value } = event.target;
    let modifiedData = [...patientInfo.used_log];

    modifiedData[index] = {
      ...modifiedData[index],
      use_case: value,
    };

    setPatientInfo({
      ...patientInfo,
      used_log: modifiedData,
    });
  };

  const handleAddHistory = () => {
    const modifiedData = [
      ...patientInfo.used_log,
      {
        use_case: '使用',
        date: dayjs(),
        used_count: 1,
      },
    ];

    const used_quantity = modifiedData.reduce((accumulator, currentValue) => {
      return accumulator + Number(currentValue.used_count);
    }, 0);

    setPatientInfo({
      ...patientInfo,
      used_log: modifiedData,
      used_quantity: used_quantity,
    });
  };

  const validate = () => {
    if (isEmpty(patientInfo.patient_number)) {
      setErrors({ ...defaultError, patient_number: false });
      notifyContext.setNotifyMessage({
        show: true,
        message: '患者IDを入力してください',
        type: 'error',
      });
      return false;
    }
    if (isEmpty(patientInfo.patient_name)) {
      setErrors({ ...defaultError, patient_name: false });
      notifyContext.setNotifyMessage({
        show: true,
        message: '患者名を入力してください',
        type: 'error',
      });
      return false;
    }
    if (
      patientInfo.creation_date?.format('YYYY/MM/DD').toString() ===
      'Invalid Date'
    ) {
      setErrors({ ...defaultError, creation_date: false });
      notifyContext.setNotifyMessage({
        show: true,
        message: '製造日を入力してください',
        type: 'error',
      });
      return false;
    }
    if (
      patientInfo.expiry_date?.format('YYYY/MM/DD').toString() ===
      'Invalid Date'
    ) {
      setErrors({ ...defaultError, expiry_date: false });
      notifyContext.setNotifyMessage({
        show: true,
        message: '保管期限を入力してください',
        type: 'error',
      });
      return false;
    }
    if (
      patientInfo.storage_date?.format('YYYY/MM/DD').toString() ===
      'Invalid Date'
    ) {
      setErrors({ ...defaultError, storage_date: false });
      notifyContext.setNotifyMessage({
        show: true,
        message: '消費期限を入力してください',
        type: 'error',
      });
      return false;
    }
    if (isEmpty(patientInfo.total_quantity)) {
      setErrors({ ...defaultError, total_quantity: false });
      notifyContext.setNotifyMessage({
        show: true,
        message: '箱内個数を入力してください',
        type: 'error',
      });
      return false;
    }
    let logDateError = false;
    patientInfo.used_log.map((log) => {
      if (log.date?.format('YYYY/MM/DD').toString() === 'Invalid Date') {
        setErrors({ ...defaultError, log_date: false });
        logDateError = true;
      }
      return log;
    });
    if (logDateError) {
      notifyContext.setNotifyMessage({
        show: true,
        message: '使用日を入力してください',
        type: 'error',
      });
      return false;
    }

    setErrors(defaultError);

    notifyContext.setNotifyMessage({
      show: false,
      message: '',
      type: 'error',
    });

    return true;
  };

  const handleUpdate = () => {
    try {
      if (!validate()) {
        return;
      }
      const token = getToken();

      if (!token) {
        throw new Error('Failed to get access token');
      }
      // const used_quantity = patientInfo.used_log.reduce(
      //   (accumulator, currentValue) => {
      //     return accumulator + Number(currentValue.used_count);
      //   },
      //   0
      // );

      const used_log = patientInfo.used_log.map((log) => {
        return {
          ...log,
          date: log.date?.format('YYYY/MM/DD') || getToday(),
        };
      });

      const data = {
        ...patientInfo,
        creation_date:
          patientInfo.creation_date?.format('YYYY/MM/DD') || getToday(),
        expiry_date:
          patientInfo.expiry_date?.format('YYYY/MM/DD') || getToday(),
        storage_date:
          patientInfo.storage_date?.format('YYYY/MM/DD') || getToday(),
        user_id: '1',
        // used_quantity: used_quantity,
        used_log: used_log,
      };

      putAPI(`/inventory/${patientInfo.barcode}?token=${token}`, data)
        .then((res: any) => {
          if (res.code === 200) {
            console.log(res);
            notifyContext.setNotifyMessage({
              show: true,
              message: '保存出来ました。',
              type: 'success',
            });
            // props.handleConfirm(res.data);
          } else {
            notifyContext.setNotifyMessage({
              show: true,
              message: res.msg || 'Failed to post data',
              type: 'error',
            });
          }
        })
        .finally(() => {});
    } catch (error) {
      console.error('Failed to fetch data:', error);
      throw error;
    }
  };

  const handleClose = () => {
    setPatientInfo(DEFAULT_DATA);
    props.handleClose();
  };

  return (
    <Dialog
      open={props.open}
      onClose={handleClose}
      aria-labelledby='alert-dialog-title'
      aria-describedby='alert-dialog-description'
      sx={{
        '& .MuiDialog-paper': {
          width: '100%',
          maxWidth: '720px',
          m: '0px',
        },
      }}
    >
      <DialogTitle id='scroll-dialog-title'>
        <Stack
          direction={'row'}
          justifyContent={'space-between'}
          alignItems={'center'}
        >
          <Typography fontSize={'20px'} fontWeight={500}>
            PRP在庫登録
          </Typography>
          <IconButton
            aria-label='toggle password visibility'
            edge='end'
            onClick={handleClose}
          >
            <Box
              component={'img'}
              src='/images/ic-close.svg'
              alt='icon eye'
              sx={{ width: '24px', height: '24px' }}
            />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ width: '720px' }}>
        <Stack alignItems={'center'}>
          <Stack direction={'row'} spacing={'50px'}>
            <Stack alignItems={'center'}>
              <Barcode value={patientInfo?.barcode} />
              <BtnGradient
                text='印刷'
                handleButtonClick={props.handleConfirm}
                sx={{ mt: '10px' }}
              />
            </Stack>
            <Stack>
              <Stack
                alignItems={'center'}
                sx={{
                  border: '1px solid #434154',
                  borderRadius: '8px',
                  p: '4px',
                }}
                width={'130px'}
                height={'112px'}
              >
                <Typography fontSize={'20px'} fontWeight={500}>
                  現在の残数
                </Typography>
                <Typography fontSize={'50px'} fontWeight={500}>
                  {patientInfo?.total_quantity - patientInfo?.used_quantity}
                </Typography>
              </Stack>
            </Stack>
          </Stack>
          <Grid container spacing={6} sx={{ m: 0, px: '20px' }}>
            <Grid item xs={6}>
              <Grid container spacing={3}>
                <Grid item xs={4}>
                  <Stack justifyContent={'center'} height={'100%'}>
                    <Typography fontWeight={500}>患者ID</Typography>
                  </Stack>
                </Grid>
                <Grid item xs>
                  <OutlinedInput
                    placeholder={'入力してください'}
                    sx={inputStyle}
                    inputProps={{
                      sx: {
                        backgroundColor: 'primary.light',
                      },
                    }}
                    name='patient_number'
                    value={patientInfo?.patient_number}
                    onChange={(event) => handleInputChange(event)}
                    error={!errors.patient_number}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={3}>
                <Grid item xs={4}>
                  <Stack justifyContent={'center'} height={'100%'}>
                    <Typography fontWeight={500}>患者名</Typography>
                  </Stack>
                </Grid>
                <Grid item xs>
                  <OutlinedInput
                    placeholder={'入力してください'}
                    sx={inputStyle}
                    inputProps={{
                      sx: {
                        backgroundColor: 'primary.light',
                      },
                    }}
                    name='patient_name'
                    value={patientInfo?.patient_name}
                    onChange={(event) => handleInputChange(event)}
                    error={!errors.patient_name}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={3}>
                <Grid item xs={4}>
                  <Stack justifyContent={'center'} height={'100%'}>
                    <Typography fontWeight={500}>製造日</Typography>
                  </Stack>
                </Grid>
                <Grid item xs>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      name=''
                      value={patientInfo?.creation_date}
                      onChange={(newValue) =>
                        handleDateChange(newValue, 'creation_date')
                      }
                      format='YYYY/MM/DD'
                      sx={dateStyle}
                    />
                  </LocalizationProvider>
                </Grid>
              </Grid>
              <Grid container spacing={3}>
                <Grid item xs={4}>
                  <Stack justifyContent={'center'} height={'100%'}>
                    <Typography fontWeight={500}>保管期限</Typography>
                  </Stack>
                </Grid>
                <Grid item xs>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      name=''
                      value={patientInfo?.expiry_date}
                      onChange={(newValue) =>
                        handleDateChange(newValue, 'expiry_date')
                      }
                      format='YYYY/MM/DD'
                      sx={dateStyle}
                    />
                  </LocalizationProvider>
                </Grid>
              </Grid>
              <Grid container spacing={3}>
                <Grid item xs={4}>
                  <Stack justifyContent={'center'} height={'100%'}>
                    <Typography fontWeight={500}>消費期限</Typography>
                  </Stack>
                </Grid>
                <Grid item xs>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      name='storage_date'
                      value={patientInfo?.storage_date}
                      onChange={(newValue) =>
                        handleDateChange(newValue, 'storage_date')
                      }
                      format='YYYY/MM/DD'
                      sx={dateStyle}
                    />
                  </LocalizationProvider>
                </Grid>
              </Grid>
              <Grid container spacing={3}>
                <Grid item xs={4}>
                  <Stack justifyContent={'center'} height={'100%'}>
                    <Typography fontWeight={500}>箱内個数</Typography>
                  </Stack>
                </Grid>
                <Grid item xs>
                  <OutlinedInput
                    placeholder={'入力してください'}
                    sx={inputStyle}
                    inputProps={{
                      sx: {
                        backgroundColor: 'primary.light',
                      },
                    }}
                    type='number'
                    name='total_quantity'
                    value={patientInfo?.total_quantity}
                    onChange={(event) => handleInputChange(event)}
                    error={!errors.total_quantity}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={6}>
              <Grid container spacing={3} mb={'8px'}>
                <Grid item xs={4}>
                  <Stack justifyContent={'center'} height={'100%'}>
                    <Typography fontSize={'20px'} fontWeight={500}>
                      使用履歴
                    </Typography>
                  </Stack>
                </Grid>
                <Grid item xs>
                  <BtnOutlineGradient
                    text='使用履歴を追加'
                    handleButtonClick={handleAddHistory}
                    width='100px'
                  />
                </Grid>
              </Grid>
              <TableContainer
                component={Paper}
                sx={{
                  '& .MuiTableCell-root': { p: '2px' },
                  '&.MuiPaper-root.MuiTableContainer-root': {
                    boxShadow: 'none',
                  },
                }}
              >
                <Table aria-label='simple table'>
                  <TableHead>
                    <TableRow>
                      <TableCell>使用日</TableCell>
                      <TableCell>用途</TableCell>
                      <TableCell>個数</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {patientInfo.used_log.map((log: any, index) => {
                      return (
                        <TableRow
                          key={index}
                          sx={{
                            '&:last-child td, &:last-child th': { border: 0 },
                          }}
                        >
                          <TableCell>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                              <DatePicker
                                name=''
                                value={dayjs(log.date)}
                                onChange={(newValue) =>
                                  handleDateLogChange(newValue, index)
                                }
                                format='YYYY/MM/DD'
                                sx={dateStyle}
                              />
                            </LocalizationProvider>
                          </TableCell>
                          <TableCell>
                            <Select
                              labelId='demo-simple-select-label'
                              id='demo-simple-select'
                              value={log.use_case}
                              onChange={(event) =>
                                handleStatusLogChange(event, index)
                              }
                              sx={{
                                backgroundColor: `${
                                  log.use_case === '使用'
                                    ? '#23B7E5'
                                    : '#ED5565'
                                }`,
                                color: '#FFF',
                                '& .MuiOutlinedInput-notchedOutline': {
                                  border: 'none',
                                },
                                '& .MuiSvgIcon-root.MuiSelect-icon': {
                                  color: '#FFF',
                                },
                                '& .MuiInputBase-input.MuiOutlinedInput-input':
                                  {
                                    p: '6px 32px 6px 14px',
                                  },
                              }}
                            >
                              <MenuItem value={'使用'}>使用</MenuItem>
                              <MenuItem value={'破棄'}>破棄</MenuItem>
                            </Select>
                          </TableCell>
                          <TableCell>
                            <OutlinedInput
                              type='number'
                              placeholder={'入力してください'}
                              sx={{ ...inputStyle, width: '60px' }}
                              inputProps={{
                                sx: {
                                  backgroundColor: 'primary.light',
                                },
                              }}
                              name='used_count'
                              value={log.used_count}
                              onChange={(event) =>
                                handleInputLogChange(event, index)
                              }
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: '20px' }}>
        <BtnOutlineGradient text='キャンセル' handleButtonClick={handleClose} />
        <BtnGradient text='保存' handleButtonClick={handleUpdate} />
      </DialogActions>
    </Dialog>
  );
};

export default DialogEditInformation;
